/**
 * Abstract Worker Implementation
 * Base class for all worker implementations that contains shared functionality
 */

import { Logger, LogLevel } from '@/shared/logger/logger';

import { BaseTaskProcessor } from './task_handler';
import { initializeWorkerWithAllApproaches } from './worker_initialization';
import {
  BaseWorkerMessage,
  BaseWorkerResponse,
  TaskResult,
} from './worker_utils';

/**
 * Abstract worker class that cannot be instantiated directly
 * Provides shared functionality for all worker types
 */
export abstract class AbstractWorker<PayloadType = any> {
  /**
   * Worker name (e.g., 'EWS', 'Events')
   */
  protected abstract readonly workerName: string;

  /**
   * Bundle name for the worker (e.g., 'ews', 'events')
   */
  protected abstract readonly bundleName: string;

  /**
   * Task processor instance responsible for handling tasks
   */
  protected abstract readonly taskProcessor: BaseTaskProcessor;

  /**
   * Error origin identifier - will be updated once we have the environment
   */
  protected errorOrigin = 'env_unknown_worker_error';

  /**
   * Worker origin identifier
   */
  protected workerOrigin = '';

  /**
   * Performance metrics
   */
  protected processedTasks = 0;
  protected failedTasks = 0;
  protected lastActivity = Date.now();
  protected readonly startTime = Date.now();

  /**
   * Handle task by delegating to task processor
   */
  protected async handleTask(
    type: string,
    payload: PayloadType,
  ): Promise<TaskResult> {
    return this.taskProcessor.processTask(type, payload);
  }

  /**
   * Initialize the worker
   * Sets up event listeners and initializes the worker
   */
  protected async initialize(): Promise<void> {
    try {
      // Initialize Logger for worker
      Logger.setLevel(LogLevel.INFO);

      // Use the unified initialization approach
      this.workerOrigin = await initializeWorkerWithAllApproaches(
        this.workerName,
        this.bundleName,
      );

      // Update error origin with the correct environment
      this.errorOrigin = `env_${Logger.environment || 'unknown'}_${this.bundleName}_worker_error`;

      Logger.debug(
        `${this.workerName} Worker fully initialized with origin: ${JSON.stringify(
          {
            location: self.location?.href,
            origin: self.location?.origin,
            timeStart: Date.now(),
            userAgent: self.navigator?.userAgent,
          },
        )}`,
      );

      // Log diagnostic information for debugging
      Logger.debug('Worker diagnostic info:', {
        location: self.location?.href,
        timeStart: Date.now(),
        userAgent: self.navigator?.userAgent,
      });

      // Set up error handling
      this.setupErrorHandling();

      // Set up message handling
      this.setupMessageHandling();

      // Log when the worker is initialized
      Logger.info('Worker initialized with API capabilities');
      Logger.info('Worker bundle loaded successfully');
    } catch (error) {
      Logger.error('Failed to initialize worker:', error);
    }
  }

  /**
   * Set up error handling for the worker
   */
  private setupErrorHandling(): void {
    self.addEventListener('error', (event: ErrorEvent) => {
      Logger.error('Worker error occurred:', {
        colno: event.colno,
        filename: event.filename,
        lineno: event.lineno,
        message: event.message,
        stack: event.error?.stack,
      });

      try {
        // Try to report the error back to the main thread
        self.postMessage({
          details: {
            colno: event.colno,
            filename: event.filename,
            lineno: event.lineno,
            message: event.message,
            stack: event.error?.stack,
          },
          error: `Worker error: ${event.message}`,
          id: 'worker_error',
          origin: this.errorOrigin,
          success: false,
          type: 'ERROR',
          // eslint-disable-next-line unicorn/require-post-message-target-origin
        });
      } catch (error) {
        Logger.error('Failed to report error to main thread:', error);
      }

      // Prevent the default error handling
      event.preventDefault();
    });

    self.addEventListener(
      'unhandledrejection',
      (event: PromiseRejectionEvent) => {
        Logger.error('Unhandled promise rejection in worker:', event.reason);

        try {
          // Report the error to the main thread
          self.postMessage({
            details: {
              reason: String(event.reason),
            },
            error: 'Unhandled promise rejection in worker',
            id: 'worker_rejection',
            origin: this.errorOrigin,
            success: false,
            type: 'ERROR',
            // eslint-disable-next-line unicorn/require-post-message-target-origin
          });
        } catch (error) {
          Logger.error(
            'Failed to report unhandled rejection to main thread:',
            error,
          );
        }

        // Prevent default handling
        event.preventDefault();
      },
    );
  }

  /**
   * Set up message handling for the worker
   */
  private setupMessageHandling(): void {
    self.addEventListener(
      'message',
      async (event: MessageEvent<BaseWorkerMessage>) => {
        const { id, origin, payload, type } = event.data;
        const taskStartTime = Date.now();

        Logger.info(`Processing ${type}`, {
          id,
          origin,
          workerOrigin: this.workerOrigin,
        });
        this.lastActivity = Date.now();

        let response: BaseWorkerResponse = {
          id,
          origin: origin || this.workerOrigin,
          success: false,
          type: `${type}_RESPONSE`,
        };

        try {
          // Special case for status request
          if (type === 'GET_STATUS') {
            response.success = true;
            response.data = {
              failedTasks: this.failedTasks,
              lastActivity: this.lastActivity,
              processedTasks: this.processedTasks,
              uptime: Date.now() - this.startTime,
            };
          } else {
            // Handle the task using the abstract method
            const result = await this.handleTask(type, payload);

            response.success = result.success;
            response.data = result.data;
            response.error = result.error;

            if (result.success) {
              this.processedTasks++;
              Logger.info(`Successfully processed ${type}`);
            } else {
              Logger.warn(`Task ${type} failed: ${result.error}`);
              this.failedTasks++;
            }
          }
        } catch (error: unknown) {
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          const errorStack = error instanceof Error ? error.stack : undefined;

          Logger.error(`Error processing ${type}:`, error);
          response.error = errorMessage;
          response.details = { stack: errorStack };
          response.success = false;
          this.failedTasks++;
        } finally {
          // Calculate task processing time
          const processingTime = Date.now() - taskStartTime;
          response.data = response.data || {};

          // Add metadata about processing time
          if (typeof response.data === 'object') {
            response.data._meta = {
              ...response.data._meta,
              processingTime,
              timestamp: Date.now(),
            };
          }

          // Log response details before sending
          Logger.debug(`[Worker] Sending response:`, {
            id: response.id,
            origin: response.origin,
            requestOrigin: origin,
            type: response.type,
          });

          // Send response back to main thread
          // eslint-disable-next-line unicorn/require-post-message-target-origin
          self.postMessage(response);
        }
      },
    );
  }
}
